import { useTranslation } from "react-i18next";
import { PracticeProfilesEditor } from "../../../../components/practice/PracticeProfilesEditor";
import { openDesktopSettings } from "../../../../domains/app/stores/desktopSettingsStore";
import { getItemTranslation } from "../../../../domains/app/utils/getItemTranslation";
import type { CurrentUser } from "../../../../domains/user/hooks/useCurrentUser";
import { SettingsPage, SettingsSection } from "../components/SettingsPanel";

/**
 * My practice (Figma "D3.settings.practice"): the specialty the practices
 * belong to, with its way to My main specialty, then the practices.
 *
 * Hosts the SAME editor the mobile sheet and the signup carousel host. The
 * practice rules are not re-implemented here: a second answer to "what is my
 * feed about" is exactly what this screen exists to prevent.
 *
 * The `-mx-5` cancels the editor's own 20px gutter so its rows line up with the
 * panel's reading column. It is a shared component with a mobile home, and
 * re-padding it for one host would move it for every other.
 */
export function PracticeSection({
  currentUser,
}: {
  currentUser: CurrentUser;
}): React.JSX.Element {
  const { t } = useTranslation();
  const specialty = currentUser.mainSpecialtyModel;
  return (
    <SettingsPage back={null}>
      <SettingsSection
        title={t("My practice")}
        description={t("Here are the practices we show you publications for.")}
      >
        {specialty === null ? null : (
          <div className="mt-2.5 flex items-center gap-3 rounded-xl bg-ds-slate-100 px-4 py-3">
            <span className="min-w-0 flex-1 text-sm font-semibold text-ds-slate-950">
              {getItemTranslation(specialty)}
            </span>
            <button
              type="button"
              onClick={() => {
                openDesktopSettings({ page: "specialty" });
              }}
              className="cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-ds-mint-800 underline"
            >
              {t("Edit")}
            </button>
          </div>
        )}
        <div className="-mx-5 flex min-h-0 flex-col">
          <PracticeProfilesEditor
            // Settings, so no campaign guess — same reasoning as the sheet.
            seedFromInference={false}
            surface="desktop"
            onSaved={() => {
              // Stay on the panel: the toast confirms, and the nav is the way out.
              // Nothing covers the rail here for the save to have to uncover,
              // which is why the sheet's host closes and this one does not.
            }}
          />
        </div>
      </SettingsSection>
    </SettingsPage>
  );
}
